import type { ValuePropsPayloadData } from './propValues.types';

/**
 * Value-prop tiles shown in the designs gallery.
 *
 * Previously served by the `printValuePropsData` remote flag, which had two variants that
 * differed by a single tile (Free Shipping, US-only). The data hadn't changed in a long time and
 * the flag was never used for an experiment, so the superset payload is inlined here and the
 * region difference is expressed per-tile via `visibleInCountries` — see {@link ValuePropAsset}.
 */
export const VALUE_PROPS_PAYLOAD: ValuePropsPayloadData = {
  props: [
    {
      // Auto-rotating "list" tile pinned to the top-left slot. Rotates through several existing
      // value props — each slide keeps its own click-through link and telemetry id. Shown globally
      // across every category (['*/*']); slides with no asset for the current breakpoint (the
      // desktop-only foil video on mobile) are dropped at render.
      slides: [
        {
          link: 'https://withjoy.com/help/en/articles/9972925-envelope-recipient-addressing-a-quick-guide',
          telemetryId: 'envelope_addressing',
          contentList: [
            {
              src: 'https://withjoy.com/media/print/vp_Envelope_Addressing_Desktop_v2.png',
              type: 'image',
              responsiveTarget: 'desktop',
            },
            {
              src: 'https://withjoy.com/media/print/vp_Envelope_Addressing_Mobile_v2.png',
              type: 'image',
              responsiveTarget: 'mobile',
            },
          ],
        },
        {
          link: 'http://withjoy.com/blog/seamlessly-integrate-online-rsvps-with-your-wedding-stationery/',
          telemetryId: 'rsvp',
          contentList: [
            {
              src: 'https://withjoy.com/media/print/vp_rsvp_desktop.png',
              type: 'image',
              responsiveTarget: 'desktop',
            },
            {
              src: 'https://withjoy.com/media/print/vp_rsvp_mobile.png',
              type: 'image',
              responsiveTarget: 'mobile',
            },
          ],
          hiddenPriceContentList: [
            {
              src: 'https://joy-public-assets-bucket.s3.us-west-2.amazonaws.com/print/value-props-hidden-price/rsvps_desktop_hiddenPrice.png',
              type: 'image',
              responsiveTarget: 'desktop',
            },
            {
              src: 'https://joy-public-assets-bucket.s3.us-west-2.amazonaws.com/print/value-props-hidden-price/rsvps_mobile_hiddenPrice.png',
              type: 'image',
              responsiveTarget: 'mobile',
            },
          ],
        },
        {
          link: 'https://withjoy.com/event/edit/cards/sample-pack',
          telemetryId: 'sample_pack',
          contentList: [
            {
              src: 'https://withjoy.com/media/print/Sample_Pack_Desktop.jpg',
              type: 'image',
              responsiveTarget: 'desktop',
            },
            {
              src: 'https://withjoy.com/media/print/Sample_Pack_Mobile.jpg',
              type: 'image',
              responsiveTarget: 'mobile',
            },
          ],
        },
        {
          link: '?pf=foil%3Dwith',
          contentList: [
            {
              type: 'video',
              videoUrl: 'https://player.vimeo.com/video/1087268851?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479',
              backgroundColor: '#D2D9CD',
              responsiveTarget: 'desktop',
            },
          ],
        },
        {
          link: 'https://withjoy.com/cards/wedding/enclosure',
          telemetryId: 'enclosure',
          contentList: [
            {
              src: 'https://withjoy.com/media/print/vp_enclosure_desktop.png',
              type: 'image',
              responsiveTarget: 'desktop',
            },
            {
              src: 'https://withjoy.com/media/print/vp_enclosure_mobile.png',
              type: 'image',
              responsiveTarget: 'mobile',
            },
          ],
        },
        {
          link: 'https://withjoy.com/event/edit/cards/wedding/thank-you?pf=size%3Dpostcard',
          telemetryId: 'thank_you_postcards',
          contentList: [
            {
              src: 'https://withjoy.com/media/print/vp_Thank_You_Postcards_Desktop_v2.png',
              type: 'image',
              responsiveTarget: 'desktop',
            },
            {
              src: 'https://withjoy.com/media/print/vp_Thank_You_Postcards_Mobile_v2.png',
              type: 'image',
              responsiveTarget: 'mobile',
            },
          ],
          hiddenPriceContentList: [
            {
              src: 'https://joy-public-assets-bucket.s3.us-west-2.amazonaws.com/print/value-props-hidden-price/Thank_You_Postcards_Desktop_hiddenPrice.png',
              type: 'image',
              responsiveTarget: 'desktop',
            },
            {
              src: 'https://joy-public-assets-bucket.s3.us-west-2.amazonaws.com/print/value-props-hidden-price/Thank_You_Postcards_Mobile_hiddenPrice.png',
              type: 'image',
              responsiveTarget: 'mobile',
            },
          ],
        },
      ],
      categoryList: ['*/*'],
      preferredPosition: {
        row: 0,
        column: 0,
      },
    },
    {
      link: 'https://withjoy.com/event/edit/cards/wedding/thank-you?pf=size%3Dpostcard',
      contentList: [
        {
          src: 'https://withjoy.com/media/print/vp_Thank_You_Postcards_Desktop_v2.png',
          type: 'image',
          responsiveTarget: 'desktop',
        },
        {
          src: 'https://withjoy.com/media/print/vp_Thank_You_Postcards_Mobile_v2.png',
          type: 'image',
          responsiveTarget: 'mobile',
        },
      ],
      hiddenPriceContentList: [
        {
          src: 'https://joy-public-assets-bucket.s3.us-west-2.amazonaws.com/print/value-props-hidden-price/Thank_You_Postcards_Desktop_hiddenPrice.png',
          type: 'image',
          responsiveTarget: 'desktop',
        },
        {
          src: 'https://joy-public-assets-bucket.s3.us-west-2.amazonaws.com/print/value-props-hidden-price/Thank_You_Postcards_Mobile_hiddenPrice.png',
          type: 'image',
          responsiveTarget: 'mobile',
        },
      ],
      telemetryId: 'thank_you_postcards',
      categoryList: ['*/thankYou'],
    },
    {
      link: 'https://withjoy.com/event/edit/cards/wedding/save-the-date?pf=size%3Dpostcard',
      contentList: [
        {
          src: 'https://withjoy.com/media/print/vp_StD_Postcards_Desktop.png',
          type: 'image',
          responsiveTarget: 'desktop',
        },
        {
          src: 'https://withjoy.com/media/print/vp_StD_Postcards_Mobile.png',
          type: 'image',
          responsiveTarget: 'mobile',
        },
      ],
      hiddenPriceContentList: [
        {
          src: 'https://joy-public-assets-bucket.s3.us-west-2.amazonaws.com/print/value-props-hidden-price/StD_Postcards_Desktop_hiddenPrice.png',
          type: 'image',
          responsiveTarget: 'desktop',
        },
        {
          src: 'https://joy-public-assets-bucket.s3.us-west-2.amazonaws.com/print/value-props-hidden-price/StD_Postcards_Mobile_hiddenPrice.png',
          type: 'image',
          responsiveTarget: 'mobile',
        },
      ],
      telemetryId: 'std_postcards',
      categoryList: ['*/saveTheDate'],
    },
    {
      link: 'http://withjoy.com/blog/seamlessly-integrate-online-rsvps-with-your-wedding-stationery/',
      contentList: [
        {
          src: 'https://withjoy.com/media/print/vp_rsvp_desktop.png',
          type: 'image',
          responsiveTarget: 'desktop',
        },
        {
          src: 'https://withjoy.com/media/print/vp_rsvp_mobile.png',
          type: 'image',
          responsiveTarget: 'mobile',
        },
      ],
      hiddenPriceContentList: [
        {
          src: 'https://joy-public-assets-bucket.s3.us-west-2.amazonaws.com/print/value-props-hidden-price/rsvps_desktop_hiddenPrice.png',
          type: 'image',
          responsiveTarget: 'desktop',
        },
        {
          src: 'https://joy-public-assets-bucket.s3.us-west-2.amazonaws.com/print/value-props-hidden-price/rsvps_mobile_hiddenPrice.png',
          type: 'image',
          responsiveTarget: 'mobile',
        },
      ],
      telemetryId: 'rsvp',
      categoryList: ['*/saveTheDate', '*/invitation'],
    },
    {
      link: 'https://withjoy.com/cards/wedding/enclosure',
      contentList: [
        {
          src: 'https://withjoy.com/media/print/vp_enclosure_desktop.png',
          type: 'image',
          responsiveTarget: 'desktop',
        },
        {
          src: 'https://withjoy.com/media/print/vp_enclosure_mobile.png',
          type: 'image',
          responsiveTarget: 'mobile',
        },
      ],
      telemetryId: 'enclosure',
      categoryList: ['*/saveTheDate', '*/invitation'],
    },
    {
      link: '?pf=foil%3Dwith',
      columns: 1,
      contentList: [
        {
          type: 'video',
          videoUrl: 'https://player.vimeo.com/video/1087268851?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479',
          backgroundColor: '#D2D9CD',
          responsiveTarget: 'desktop',
        },
      ],
      categoryList: ['*/saveTheDate', '*/invitation', '*/thankYou'],
    },
    {
      link: 'https://withjoy.com/event/edit/cards/sample-pack',
      contentList: [
        {
          src: 'https://withjoy.com/media/print/Sample_Pack_Desktop.jpg',
          type: 'image',
          responsiveTarget: 'desktop',
        },
        {
          src: 'https://withjoy.com/media/print/Sample_Pack_Mobile.jpg',
          type: 'image',
          responsiveTarget: 'mobile',
        },
      ],
      telemetryId: 'sample_pack',
      categoryList: ['*/saveTheDate', '*/invitation', '*/enclosure'],
    },
    {
      link: 'https://withjoy.com/help/en/articles/10034400-joy-print-price-matching?srsltid=AfmBOooIQeMJLcddsCelttdDkN-hpPvCwWGKEgSYyRX94sq752e5aLo6',
      contentList: [
        {
          src: 'https://withjoy.com/media/print/price_match_desktop.png',
          type: 'image',
          responsiveTarget: 'desktop',
        },
        {
          src: 'https://withjoy.com/media/print/price_match_mobile.png',
          type: 'image',
          responsiveTarget: 'mobile',
        },
      ],
      hiddenPriceContentList: [
        {
          src: 'https://joy-public-assets-bucket.s3.us-west-2.amazonaws.com/print/value-props-hidden-price/Price_Match_Desktop_hiddenPrice.png',
          type: 'image',
          responsiveTarget: 'desktop',
        },
        {
          src: 'https://joy-public-assets-bucket.s3.us-west-2.amazonaws.com/print/value-props-hidden-price/Price_Match_Mobile_hiddenPrice.png',
          type: 'image',
          responsiveTarget: 'mobile',
        },
      ],
      telemetryId: 'price_matching',
      categoryList: ['*/saveTheDate', '*/invitation', '*/thankYou', '*/enclosure', '*/napkin', '*/sign', '*/menu'],
    },
    {
      contentList: [
        {
          src: 'https://withjoy.com/media/print/Card_Back_Desktop.png',
          type: 'image',
          responsiveTarget: 'desktop',
        },
        {
          src: 'https://withjoy.com/media/print/Card_Back_Mobile.png',
          type: 'image',
          responsiveTarget: 'mobile',
        },
      ],
      hiddenPriceContentList: [
        {
          src: 'https://joy-public-assets-bucket.s3.us-west-2.amazonaws.com/print/value-props-hidden-price/Card_Back_Desktop_hiddenPrice.png',
          type: 'image',
          responsiveTarget: 'desktop',
        },
        {
          src: 'https://joy-public-assets-bucket.s3.us-west-2.amazonaws.com/print/value-props-hidden-price/Card_Back_Mobile_hiddenPrice.png',
          type: 'image',
          responsiveTarget: 'mobile',
        },
      ],
      telemetryId: 'card_back',
      categoryList: ['*/saveTheDate', '*/invitation', '*/thankYou', '*/enclosure'],
    },
    {
      link: 'https://withjoy.com/help/en/articles/9972925-envelope-recipient-addressing-a-quick-guide',
      contentList: [
        {
          src: 'https://withjoy.com/media/print/vp_Envelope_Addressing_Desktop_v2.png',
          type: 'image',
          responsiveTarget: 'desktop',
        },
        {
          src: 'https://withjoy.com/media/print/vp_Envelope_Addressing_Mobile_v2.png',
          type: 'image',
          responsiveTarget: 'mobile',
        },
      ],
      telemetryId: 'envelope_addressing',
      categoryList: ['*/saveTheDate', '*/invitation', '*/thankYou'],
    },
    {
      link: 'http://withjoy.com/help/en/articles/10317109-how-to-add-qr-codes-and-links-to-cards',
      contentList: [
        {
          src: 'https://withjoy.com/media/print/vp_QR_Code_Desktop.png',
          type: 'image',
          responsiveTarget: 'desktop',
        },
        {
          src: 'https://withjoy.com/media/print/vp_QR_Code_Mobile.png',
          type: 'image',
          responsiveTarget: 'mobile',
        },
      ],
      telemetryId: 'qr_code',
      categoryList: ['*/saveTheDate', '*/invitation', '*/thankYou', '*/enclosure'],
    },
    {
      link: 'https://withjoy.com/help/en/articles/9438227-joy-print',
      contentList: [
        {
          src: 'https://withjoy.blob.core.windows.net/print/assets/valueProps/Edit_Personalize_Desktop.png',
          type: 'image',
          responsiveTarget: 'desktop',
        },
        {
          src: 'https://withjoy.blob.core.windows.net/print/assets/valueProps/Edit_Personalize_Mobile.png',
          type: 'image',
          responsiveTarget: 'mobile',
        },
      ],
      telemetryId: 'edit_personalize',
      categoryList: ['*/*'],
    },
    {
      link: 'https://withjoy.com/help/en/articles/9765459-premium-invitation-suites',
      contentList: [
        {
          src: 'https://withjoy.com/media/print/Premium_Suites_Desktop.png',
          type: 'image',
          responsiveTarget: 'desktop',
        },
        {
          src: 'https://withjoy.com/media/print/Premium_Suites_Mobile.png',
          type: 'image',
          responsiveTarget: 'mobile',
        },
      ],
      telemetryId: 'design_concierge',
      categoryList: ['*/saveTheDate', '*/invitation', '*/thankYou'],
    },
    {
      link: 'https://withjoy.com/help/en/articles/8309436-joy-101',
      contentList: [
        {
          src: 'https://withjoy.blob.core.windows.net/print/assets/valueProps/Matching_Website_Desktop.png',
          type: 'image',
          responsiveTarget: 'desktop',
        },
        {
          src: 'https://withjoy.blob.core.windows.net/print/assets/valueProps/Matching_Website_Mobile.png',
          type: 'image',
          responsiveTarget: 'mobile',
        },
      ],
      telemetryId: 'matching_website',
      categoryList: ['*/saveTheDate', '*/invitation', '*/thankYou', '*/enclosure'],
    },
    {
      link: 'https://withjoy.com/help/en/articles/10168216-designing-and-sending-premium-digital-cards',
      contentList: [
        {
          src: 'https://withjoy.com/media/print/vp_Paper_Digital_Desktop.png',
          type: 'image',
          responsiveTarget: 'desktop',
        },
        {
          src: 'https://withjoy.com/media/print/vp_Paper_Digital_Mobile.png',
          type: 'image',
          responsiveTarget: 'mobile',
        },
      ],
      telemetryId: 'paper_digital',
      categoryList: ['*/saveTheDate', '*/invitation', '*/thankYou', '*/enclosure'],
    },
    {
      contentList: [
        {
          src: 'https://withjoy.com/media/print/Free_Shipping_Desktop.jpg',
          type: 'image',
          responsiveTarget: 'desktop',
        },
        {
          src: 'https://withjoy.com/media/print/Free_Shipping_Mobile.jpg',
          type: 'image',
          responsiveTarget: 'mobile',
        },
      ],
      telemetryId: 'free-shipping',
      categoryList: ['*/saveTheDate', '*/invitation', '*/thankYou', '*/enclosure', '*/menu', '*/napkin', '*/sign'],
      visibleInCountries: ['US'],
    },
    {
      link: 'http://withjoy.com/help/en/articles/10317328-how-to-choose-the-right-paper-type',
      contentList: [
        {
          src: 'https://withjoy.com/media/print/vp_Superb_Quality_Desktop.png',
          type: 'image',
          responsiveTarget: 'desktop',
        },
        {
          src: 'https://withjoy.com/media/print/vp_Superb_Quality_Mobile.png',
          type: 'image',
          responsiveTarget: 'mobile',
        },
      ],
      telemetryId: 'superb_quality',
      categoryList: ['*/saveTheDate', '*/invitation', '*/thankYou', '*/enclosure'],
    },
    {
      contentList: [
        {
          src: 'https://withjoy.blob.core.windows.net/print/assets/valueProps/testimonial_desktop.png',
          type: 'image',
          responsiveTarget: 'desktop',
        },
        {
          src: 'https://withjoy.blob.core.windows.net/print/assets/valueProps/testimonial_mobile.png',
          type: 'image',
          responsiveTarget: 'mobile',
        },
      ],
      telemetryId: 'testimonial',
      categoryList: ['*/saveTheDate', '*/invitation', '*/thankYou', '*/enclosure', '*/menu', '*/napkin', '*/sign'],
    },
    {
      link: 'https://withjoy.typeform.com/to/i0cqrujN',
      contentList: [
        {
          src: 'https://withjoy.com/media/print/vp_Custom_Design_Desktop_v2.png',
          type: 'image',
          responsiveTarget: 'desktop',
        },
        {
          src: 'https://withjoy.com/media/print/vp_Custom_Design_Mobile_v2.png',
          type: 'image',
          responsiveTarget: 'mobile',
        },
      ],
      categoryList: [],
    },
    {
      link: 'https://withjoy.com/help/en/articles/9147305-contact-collector-a-step-by-step-guide',
      contentList: [
        {
          src: 'https://withjoy.com/media/print/vp_Contact_Collector_Desktop.png',
          type: 'image',
          responsiveTarget: 'desktop',
        },
        {
          src: 'https://withjoy.com/media/print/vp_Contact_Collector_Mobile.png',
          type: 'image',
          responsiveTarget: 'mobile',
        },
      ],
      telemetryId: 'request_custom_design',
      categoryList: ['*/saveTheDate', '*/invitation', '*/thankYou', '*/enclosure'],
    },
    {
      contentList: [
        {
          src: 'https://withjoy.com/media/print/holiday_vp_contact_collector.jpg',
          type: 'image',
          responsiveTarget: 'desktop',
        },
        {
          src: 'https://withjoy.com/media/print/holiday_vp_contact_collector.jpg',
          type: 'image',
          responsiveTarget: 'mobile',
        },
      ],
      categoryList: ['*/holiday'],
    },
    {
      contentList: [
        {
          src: 'https://withjoy.com/media/print/holiday_vp_customize.jpg',
          type: 'image',
          responsiveTarget: 'desktop',
        },
        {
          src: 'https://withjoy.com/media/print/holiday_vp_customize.jpg',
          type: 'image',
          responsiveTarget: 'mobile',
        },
      ],
      categoryList: ['*/holiday'],
    },
    {
      contentList: [
        {
          src: 'https://withjoy.com/media/print/holiday_vp_quality.jpg',
          type: 'image',
          responsiveTarget: 'desktop',
        },
        {
          src: 'https://withjoy.com/media/print/holiday_vp_quality.jpg',
          type: 'image',
          responsiveTarget: 'mobile',
        },
      ],
      categoryList: ['*/holiday'],
    },
    {
      contentList: [
        {
          src: 'https://withjoy.com/media/print/holiday_vp_price_match.jpg',
          type: 'image',
          responsiveTarget: 'desktop',
        },
        {
          src: 'https://withjoy.com/media/print/holiday_vp_price_match.jpg',
          type: 'image',
          responsiveTarget: 'mobile',
        },
      ],
      categoryList: ['*/holiday'],
    },
    {
      contentList: [
        {
          src: 'https://withjoy.com/media/print/holiday_vp_free_envelope.jpg',
          type: 'image',
          responsiveTarget: 'desktop',
        },
        {
          src: 'https://withjoy.com/media/print/holiday_vp_free_envelope.jpg',
          type: 'image',
          responsiveTarget: 'mobile',
        },
      ],
      categoryList: ['*/holiday'],
    },
    {
      contentList: [
        {
          src: 'https://withjoy.blob.core.windows.net/print/assets/valueProps/day_of_napkins.png',
          type: 'image',
          responsiveTarget: 'desktop',
        },
        {
          src: 'https://withjoy.blob.core.windows.net/print/assets/valueProps/day_of_napkins_mobile.png',
          type: 'image',
          responsiveTarget: 'mobile',
        },
      ],
      categoryList: ['*/napkin', '*/sign', '*/menu'],
    },
    {
      contentList: [
        {
          src: 'https://withjoy.blob.core.windows.net/print/assets/valueProps/day_of_signs.png',
          type: 'image',
          responsiveTarget: 'desktop',
        },
        {
          src: 'https://withjoy.blob.core.windows.net/print/assets/valueProps/day_of_signs_mobile.png',
          type: 'image',
          responsiveTarget: 'mobile',
        },
      ],
      categoryList: ['*/napkin', '*/sign', '*/menu'],
    },
    {
      link: '?pf=style%3DtaylorTravis',
      contentList: [
        {
          src: 'https://withjoy.com/media/print/tayvis_desktop.png',
          type: 'image',
          responsiveTarget: 'desktop',
        },
        {
          src: 'https://withjoy.com/media/print/tayvis_mobile.png',
          type: 'image',
          responsiveTarget: 'mobile',
        },
      ],
      telemetryId: 'taylorTravis',
      categoryList: ['*/saveTheDate', '*/invitation'],
    },
    {
      contentList: [
        {
          src: 'https://withjoy.blob.core.windows.net/print/assets/valueProps/day_of_menu.png',
          type: 'image',
          responsiveTarget: 'desktop',
        },
        {
          src: 'https://withjoy.blob.core.windows.net/print/assets/valueProps/day_of_menu_mobile.png',
          type: 'image',
          responsiveTarget: 'mobile',
        },
      ],
      categoryList: ['*/napkin', '*/sign', '*/menu'],
    },
  ],
  repeatTiles: true,
  randomizeOrder: true,
};
